import { FC, useCallback, useEffect, useState } from 'react';

import { Select } from 'antd';
import { Button as AntBtn } from 'antd';
import { Button, Input, Label, Table, message } from 'ui';

import { useDeleteOfferTicketTypeMutation } from 'services/promoter/promoterApiService';

import { EventTicketType } from 'types/events/eventTypes';
import { OfferType } from 'types/promoters/promoterTypes';

import s from './EventOfferSetTicketType.module.scss';

interface EventOfferSetTicketTypeProps {
  tickets?: EventTicketType[];
  offerType?: OfferType;
  ticketTypes?: {
    ticketType?: EventTicketType;
    ticketTypeId?: string;
    id?: string;
    priceToBeSold?: number;
    minimumNumberOfTicketsToBeBought?: number;
  }[];
  setTicketTypes: (
    ticketTypes: {
      id: string;
      ticketType: EventTicketType;
      priceToBeSold?: number;
      minimumNumberOfTicketsToBeBought?: number;
    }[],
  ) => void;
}

const EventOfferSetTicketType: FC<EventOfferSetTicketTypeProps> = ({
  tickets,
  offerType,
  setTicketTypes,
  ticketTypes,
}) => {
  const [deleteOfferTicket, { isLoading }] = useDeleteOfferTicketTypeMutation();

  const [selectedTicketType, setSelectedTicketType] = useState<
    {
      id: string;
      ticketType: EventTicketType;
      priceToBeSold?: number;
      minimumNumberOfTicketsToBeBought?: number;
    }[]
  >([]);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [modifyingTicketTypeID, setModifyingTicketTypeID] = useState<string | undefined>('');

  const [ticketTypeId, setTicketTypeId] = useState<string | undefined>();
  const [priceToBeSold, setPriceToBeSold] = useState<number | undefined>();
  const [offerTicketTypeId, setOfferTicketTypeId] = useState<string | undefined>();
  const [minimumTickets, setMinimumTickets] = useState<number | undefined>();

  const [errors, setErrors] = useState({
    ticketTypeId: false,
    priceToBeSold: false,
    minimumTickets: false,
  });

  useEffect(() => {
    if (tickets && ticketTypes) {
      const formatted = tickets
        .map((ticket) => {
          const matched = ticketTypes.find((type) => type.ticketTypeId === ticket.id);
          if (matched) {
            return {
              ticketType: ticket,
              id: matched.id,
              priceToBeSold:
                offerType === OfferType.fixedPriceOnSpecificTicketTypes ||
                offerType === OfferType.perDifferentShowNames
                  ? matched.priceToBeSold
                  : undefined,
              minimumNumberOfTicketsToBeBought:
                offerType === OfferType.perDifferentEvents
                  ? matched.minimumNumberOfTicketsToBeBought
                  : undefined,
            };
          }
          return null;
        })
        .filter(Boolean) as any[];
      setSelectedTicketType(formatted);
    }
  }, [ticketTypes, offerType, tickets]);

  const handleAddOrUpdate = useCallback(() => {
    const newErrors = {
      ticketTypeId: !ticketTypeId,
      priceToBeSold:
        (offerType === OfferType.fixedPriceOnSpecificTicketTypes ||
          offerType === OfferType.perDifferentShowNames) &&
        priceToBeSold == null,
      minimumTickets: offerType === OfferType.perDifferentEvents && minimumTickets == null,
    };

    setErrors(newErrors);
    setIsEditing(false);

    if (newErrors.ticketTypeId || newErrors.priceToBeSold || newErrors.minimumTickets) return;

    const ticket = tickets?.find((t) => t.id === ticketTypeId);
    if (!ticket) return;

    setModifyingTicketTypeID(ticketTypeId ?? '');

    const updatedTicketTypes = (() => {
      const exists = selectedTicketType.findIndex((item) => item.ticketType.id === ticketTypeId);

      const newEntry = {
        ticketType: ticket,
        id: offerTicketTypeId as string,
        priceToBeSold:
          offerType === OfferType.fixedPriceOnSpecificTicketTypes ||
          offerType === OfferType.perDifferentShowNames
            ? priceToBeSold
            : undefined,
        minimumNumberOfTicketsToBeBought:
          offerType === OfferType.perDifferentEvents ? minimumTickets : undefined,
      };

      return exists !== -1
        ? selectedTicketType.map((item, idx) => (idx === exists ? newEntry : item))
        : [...selectedTicketType, newEntry];
    })();

    setSelectedTicketType(updatedTicketTypes); // ✅ Update child display state
    setTicketTypes(updatedTicketTypes);

    setTicketTypeId(undefined);
    setPriceToBeSold(undefined);
    setMinimumTickets(undefined);
    setOfferTicketTypeId(undefined);
    setErrors({ ticketTypeId: false, priceToBeSold: false, minimumTickets: false });
  }, [
    ticketTypeId,
    priceToBeSold,
    selectedTicketType,
    minimumTickets,
    offerType,
    tickets,
    offerTicketTypeId,
    setErrors,
    setIsEditing,
    setModifyingTicketTypeID,
    setSelectedTicketType,
    setTicketTypes,
    setTicketTypeId,
    setPriceToBeSold,
    setMinimumTickets,
    setOfferTicketTypeId,
  ]);

  const handleEdit = (row: any) => {
    setTicketTypeId(row.ticketType.id);
    setPriceToBeSold(row.priceToBeSold);
    setOfferTicketTypeId(row.offerTicketTypeId);
    setMinimumTickets(row.minimumNumberOfTicketsToBeBought);
    setIsEditing(true);
    setErrors({ ticketTypeId: false, priceToBeSold: false, minimumTickets: false });
  };

  const handleDelete = async (tickettypeid: string, offerTicketTypeId: string) => {
    setModifyingTicketTypeID(undefined);
    if (tickettypeid === modifyingTicketTypeID) {
      const filtered = selectedTicketType.filter((item) => item.ticketType.id !== tickettypeid);
      setSelectedTicketType(filtered);
      setTicketTypes(filtered);
      return;
    }

    const res = await deleteOfferTicket(offerTicketTypeId);

    if ('data' in res) {
      message.success('Successfully deleted ticket type.');
      const filtered = selectedTicketType.filter((item) => item.id !== offerTicketTypeId);
      setSelectedTicketType(filtered);
      setTicketTypes(filtered);
    }
  };

  const columns = [
    {
      title: 'Ticket Type',
      dataIndex: 'ticketType',
      key: 'ticketType',
      render: (ticketType) => ticketType.name,
    },
    {
      title: 'Ticket Price',
      dataIndex: 'ticketType',
      key: 'ticketType',
      render: (ticketType) => `€${ticketType?.price}`,
    },
    (offerType === OfferType.fixedPriceOnSpecificTicketTypes ||
      offerType === OfferType.perDifferentShowNames) && {
      title: 'To be sold at',
      dataIndex: 'priceToBeSold',
      key: 'priceToBeSold',
      render: (priceToBeSold) => (priceToBeSold != null ? `€${priceToBeSold}` : '-'),
    },
    offerType === OfferType.perDifferentEvents && {
      title: (
        <>
          Minimum Number Of
          <br />
          Tickets to be bought
        </>
      ),
      dataIndex: 'minimumNumberOfTicketsToBeBought',
      key: 'minimumNumberOfTicketsToBeBought',
    },
    {
      title: 'Action',
      key: 'action',
      render: (row) => (
        <div className={s.actionButtons}>
          <AntBtn htmlType="button" type="link" onClick={() => handleEdit(row)}>
            Edit
          </AntBtn>
          <AntBtn
            htmlType="button"
            loading={isLoading}
            type="link"
            onClick={() => handleDelete(row.ticketType.id, row.id)}>
            Delete
          </AntBtn>
        </div>
      ),
    },
  ].filter(Boolean) as any;

  return (
    <div className={s.wrapper}>
      <div className={s.labelWrapper}>
        <Label>Choose the ticket types on which this offer should apply on.</Label>
      </div>

      <div className={s.content}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
          <div style={{ width: '100%' }}>
            <Label required>Ticket Type</Label>
            <Select
              className={`${s.ticketSelect} ${errors.ticketTypeId ? s.error : ''}`}
              options={tickets?.map((item) => ({ label: item.name, value: item.id }))}
              placeholder="Select ticket type"
              value={ticketTypeId}
              onChange={(value) => {
                setTicketTypeId(value);
                setErrors((prev) => ({
                  ...prev,
                  ticketTypeId: false,
                }));
              }}
            />
          </div>

          <div className={s.inputWrapper}>
            {(offerType === OfferType.fixedPriceOnSpecificTicketTypes ||
              offerType === OfferType.perDifferentShowNames) && (
              <div className={`${s.inputBlock} ${errors.priceToBeSold ? s.error : ''}`}>
                <Label required>Price To Be Sold</Label>
                <Input
                  name="priceTobeSold"
                  placeholder="0"
                  type="number"
                  style={{ width: '100%' }}
                  value={priceToBeSold}
                  onChange={(e) => {
                    setPriceToBeSold(Number(e.target.value));
                    setErrors((prev) => ({
                      ...prev,
                      priceToBeSold: false,
                    }));
                  }}
                />
              </div>
            )}
            {offerType === OfferType.perDifferentEvents && (
              <div className={`${s.inputBlock} ${errors.minimumTickets ? s.error : ''}`}>
                <Label required>Minimum number of tickets</Label>
                <Input
                  name="minTicket"
                  style={{ width: '100%' }}
                  placeholder="0"
                  type="number"
                  value={minimumTickets}
                  onChange={(e) => {
                    setMinimumTickets(Number(e.target.value));
                    setErrors((prev) => ({
                      ...prev,
                      minimumTickets: false,
                    }));
                  }}
                />
              </div>
            )}
          </div>

          <Button htmlType="button" fullWidth onClick={handleAddOrUpdate}>
            {isEditing ? 'Edit' : 'Add'}
          </Button>
        </div>

        <div>
          {selectedTicketType.length > 0 && (
            <Table
              dataSource={selectedTicketType}
              rowKey={(row) => row.ticketType.id}
              columns={columns}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EventOfferSetTicketType;
